/**
 * Gifts Popup — free-gift selection above a cart threshold.
 *
 * Flow:
 *   1. The in-drawer bar (snippets/gifts-popup.liquid) renders a trigger button
 *      with data-gift-allowed once the threshold is reached.
 *   2. Trigger → open the body-level modal (snippets/gifts-popup-modal.liquid),
 *      mark already-chosen gifts live via /cart.js, enforce the selection limit.
 *   3. Confirm → POST /cart/add.js with properties.__popup_gift_id = <variantId>
 *      for each chosen gift (the marking the checkout extension discounts by).
 *   4. Cleanup → when the cart drops below a threshold, the bar emits the
 *      excess line-item keys in data-gift-remove-keys; we remove them.
 *
 * Integrates with cart-drawer.js:
 *   - window.refreshCartDrawer() / window.openCart()
 *   - listens for the custom 'cart:rerendered' event it dispatches after every swap
 *
 * Hard contract: the property key __popup_gift_id must not be renamed.
 */
(function () {
  'use strict';

  var GIFT_PROP = '__popup_gift_id';

  function modalEl() { return document.getElementById('gifts-popup-modal'); }

  /* ── Selection helpers ── */

  function options() {
    var m = modalEl();
    return m ? Array.prototype.slice.call(m.querySelectorAll('.gift-option')) : [];
  }

  /* Options the user can still choose (not already a gift in the cart). */
  function selectable() {
    return options().filter(function (o) { return !o.classList.contains('is-incart'); });
  }

  function pressed(o) { return o.getAttribute('aria-pressed') === 'true'; }

  function allowed() {
    var m = modalEl();
    var a = m ? parseInt(m.getAttribute('data-allowed'), 10) : 1;
    return isNaN(a) ? 1 : a;
  }

  /* After any selection change: enforce the limit + toggle the confirm button. */
  function updateLimitUI() {
    var max = allowed();
    var chosen = selectable().filter(pressed).length;
    selectable().forEach(function (o) {
      /* When the limit is reached, grey out the remaining unselected options. */
      var disable = !pressed(o) && chosen >= max;
      o.classList.toggle('is-disabled', disable);
    });
    var m = modalEl();
    var addBtn = m ? m.querySelector('[data-gift-add]') : null;
    if (addBtn) addBtn.disabled = chosen < 1;
  }

  function toggleOption(o) {
    if (o.classList.contains('is-incart')) return;          /* already chosen — locked */
    var max = allowed();

    if (max <= 1) {
      /* Radio behaviour: choosing one clears the others. */
      var wasPressed = pressed(o);
      selectable().forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
      o.setAttribute('aria-pressed', wasPressed ? 'false' : 'true');
    } else {
      if (!pressed(o)) {
        var chosen = selectable().filter(pressed).length;
        if (chosen >= max) return;                          /* limit reached — ignore */
        o.setAttribute('aria-pressed', 'true');
      } else {
        o.setAttribute('aria-pressed', 'false');
      }
    }
    updateLimitUI();
  }

  /* ── Open / close ── */

  function openModal(allowedCount) {
    var m = modalEl();
    if (!m) return;
    m.setAttribute('data-allowed', allowedCount > 0 ? allowedCount : 1);

    /* Reset selection, then mark gifts already in the cart as locked. */
    options().forEach(function (o) {
      o.setAttribute('aria-pressed', 'false');
      o.classList.remove('is-incart', 'is-disabled');
    });

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var inCart = {};
        (cart.items || []).forEach(function (it) {
          if (it.properties && it.properties[GIFT_PROP]) inCart[String(it.id)] = true;
        });
        options().forEach(function (o) {
          if (inCart[String(o.getAttribute('data-variant-id'))]) {
            o.classList.add('is-incart');
            o.setAttribute('aria-pressed', 'true');
          }
        });
        updateLimitUI();
      })
      .catch(function () { updateLimitUI(); })
      .then(showModal);
  }

  function showModal() {
    var m = modalEl();
    if (!m) return;
    m.classList.add('open');
    m.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gifts-modal-open');
    var close = m.querySelector('.gifts-modal__close');
    if (close) close.focus();
  }

  function closeModal() {
    var m = modalEl();
    if (!m) return;
    m.classList.remove('open');
    m.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('gifts-modal-open');
  }

  /* ── Add chosen gifts ── */

  function addGifts() {
    var m = modalEl();
    if (!m) return;
    var chosen = selectable().filter(pressed);
    if (!chosen.length) return;

    var addBtn = m.querySelector('[data-gift-add]');
    if (addBtn) addBtn.classList.add('is-loading');

    var items = chosen.map(function (o) {
      var id = o.getAttribute('data-variant-id');
      var props = {};
      props[GIFT_PROP] = id;
      return { id: id, quantity: 1, properties: props };
    });

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('gift add failed');
        return r.json();
      })
      .then(function () {
        closeModal();
        if (addBtn) addBtn.classList.remove('is-loading');
        if (typeof window.openCart === 'function') window.openCart();
        else if (typeof window.refreshCartDrawer === 'function') window.refreshCartDrawer();
      })
      .catch(function (err) {
        console.error('[gifts-popup] add failed:', err);
        if (addBtn) addBtn.classList.remove('is-loading');
      });
  }

  /* ── Cleanup: remove gifts when the cart dropped below a threshold ── */

  var cleaning = false;

  function runCleanup() {
    if (cleaning) return;
    var bar = document.querySelector('[data-gift-popup]');
    if (!bar) return;
    var keys = (bar.getAttribute('data-gift-remove-keys') || '')
      .split(',').filter(function (k) { return k; });
    if (!keys.length) return;

    cleaning = true;
    var chain = Promise.resolve();
    keys.forEach(function (key) {
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      });
    });
    chain
      .then(function () {
        cleaning = false;
        if (typeof window.refreshCartDrawer === 'function') window.refreshCartDrawer();
      })
      .catch(function (err) {
        cleaning = false;
        console.error('[gifts-popup] cleanup failed:', err);
      });
  }

  /* ── Delegated events (bound once; survive drawer re-renders) ── */

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-gift-trigger]');
    if (trigger) {
      e.preventDefault();
      openModal(parseInt(trigger.getAttribute('data-gift-allowed'), 10) || 1);
      return;
    }
    if (e.target.closest('[data-gift-close]')) {
      e.preventDefault();
      closeModal();
      return;
    }
    var opt = e.target.closest('.gift-option');
    if (opt && modalEl() && modalEl().contains(opt)) {
      e.preventDefault();
      toggleOption(opt);
      return;
    }
    if (e.target.closest('[data-gift-add]')) {
      e.preventDefault();
      addGifts();
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    var m = modalEl();
    if (e.key === 'Escape' && m && m.classList.contains('open')) closeModal();
  });

  /* ── Konfetti: einmalig wenn die Schwelle überschritten wird & der Drawer offen ist ── */
  var giftWasUnlocked = false;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function playConfetti() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer || prefersReducedMotion() || drawer.querySelector('.gift-confetti-layer')) return;
    var layer = document.createElement('div');
    layer.className = 'gift-confetti-layer';
    var colors = ['#ffffff', '#f1b836', '#ff8aa0', '#7fe9e3', '#0c6b67'];
    var fall = (drawer.clientHeight || 600) + 30;
    for (var i = 0; i < 60; i++) {
      var p = document.createElement('i');
      var sz = 6 + Math.round(Math.random() * 5);
      p.style.left = Math.round(Math.random() * 100) + '%';
      p.style.width = sz + 'px';
      p.style.height = sz + 'px';
      p.style.background = colors[i % colors.length];
      if (Math.random() > 0.5) p.style.borderRadius = '50%';
      p.style.setProperty('--gc-fall', fall + 'px');
      p.style.setProperty('--gc-dur', (2 + Math.random() * 1.5).toFixed(2) + 's');     /* Fallzeit 2.0–3.5 s */
      p.style.setProperty('--gc-delay', (Math.random() * 2).toFixed(2) + 's');         /* Emission über ~2 s gestreut → kein Klumpen */
      layer.appendChild(p);
    }
    drawer.appendChild(layer);
    /* Layer entfernen, nachdem das letzte Teilchen (max. Delay + max. Fallzeit) durch ist */
    setTimeout(function () { if (layer.parentNode) layer.parentNode.removeChild(layer); }, 5800);
  }

  function maybeConfetti() {
    var bar = document.querySelector('[data-gift-popup]');
    var drawer = document.getElementById('cart-drawer');
    var unlocked = !!(bar && bar.getAttribute('data-gift-unlocked') === '1');
    if (!unlocked) { giftWasUnlocked = false; return; }            /* zurückgefallen → erneut feierbar */
    if (drawer && drawer.classList.contains('open') && !giftWasUnlocked) {
      giftWasUnlocked = true;
      playConfetti();
    }
  }

  /* Cleanup + Konfetti-Check nach Initial-Load und nach jedem Drawer-Re-Render. */
  window.addEventListener('load', function () {
    var bar = document.querySelector('[data-gift-popup]');
    /* bereits beim Laden freigeschaltet? → als „gesehen" markieren, damit es nicht beim ersten Öffnen feuert */
    giftWasUnlocked = !!(bar && bar.getAttribute('data-gift-unlocked') === '1');
    runCleanup();
  });
  document.addEventListener('cart:rerendered', function () { runCleanup(); maybeConfetti(); });
})();
